import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Checkbox, Chip, Collapse, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, IconButton, List, ListItem, ListItemIcon, ListItemText,
  Stack, Switch, Tab, Tabs, TextField, Typography,
} from '@mui/material';
import {
  DragIndicator as DragIcon,
  ArrowUpward as MoveUpIcon,
  ArrowDownward as MoveDownIcon,
  Map as MapIcon,
  BarChart as ChartIcon,
  GridView as GridIcon,
  DataObject as DataIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  CalendarMonth as CalendarIcon,
  Tag as TagIcon,
} from '@mui/icons-material';
import type {
  ChartAxisLabelMode,
  ChartColorPaletteId,
  ChartLegendMode,
  ChartLegendPlacement,
  CrossFilterTarget,
  CrossFilterFieldMapping,
  DashboardTabDefinition,
  DashboardWidgetInstance,
  DashboardWidgetStyle,
  DashboardWidgetType,
  IndicatorFilterOperator,
  WidgetGroupDirection,
  WidgetStackNavStyle,
} from './types';
import { isVizWidgetType } from './widgetGuards';
import { DEFAULT_VIZ_TABS } from './visualizationDefaults';
import {
  AppearancePanel,
  ChartOptionsPanel,
  ChartWidgetDataPanel,
  DataBindingPanel,
  GroupSettingsPanel,
  IndicatorSettingsPanel,
  SlicesPalettePanel,
  StackSettingsPanel,
} from './widgetSettingsPanels';
import { useWebMapContext } from '../webMap/WebMapContext';
import type { WidgetDataBinding, WebMapLayerConfig } from './types';
import RichTextEditor from './RichTextEditor';
import DeviceListFormatBuilder from './DeviceListFormatBuilder';
import type { DeviceFieldFormatRule } from './types';
import {
  SettingsColorPicker,
  SettingsPalettePicker,
  SettingsToggleGroup,
  SettingsSelect,
} from './controls';

type SettingsTabId = 'general' | 'content' | 'data' | 'chart' | 'slices' | 'indicator' | 'columns' | 'list' | 'stack' | 'group' | 'appearance' | 'actions';

const TAB_LABEL: Record<SettingsTabId, string> = {
  general: 'General',
  content: 'Content',
  data: 'Data',
  chart: 'Chart',
  slices: 'Slices',
  indicator: 'Indicator',
  columns: 'Columns',
  list: 'List',
  stack: 'Stack',
  group: 'Group',
  appearance: 'Appearance',
  actions: 'Actions',
};

const INDICATOR_CONFIG_KEYS = [
  'indicatorFilterField',
  'indicatorFilterOperator',
  'indicatorFilterValue',
  'indicatorTextTop',
  'indicatorTextMiddle',
  'indicatorTextBottom',
  'indicatorColorTop',
  'indicatorColorMiddle',
  'indicatorColorBottom',
  'indicatorAccent',
  'indicatorFaceBg',
  'indicatorBgMiddle',
  'indicatorShowLastUpdated',
] as const;

const WIDGET_TYPE_ICONS: Partial<Record<DashboardWidgetType, React.ReactNode>> = {
  web_map: <MapIcon fontSize="small" />,
  chart_bar: <ChartIcon fontSize="small" />,
  chart_line: <ChartIcon fontSize="small" />,
  chart_area: <ChartIcon fontSize="small" />,
  chart_donut: <ChartIcon fontSize="small" />,
  chart_radar: <ChartIcon fontSize="small" />,
  chart_scatter: <ChartIcon fontSize="small" />,
  chart_treemap: <ChartIcon fontSize="small" />,
  viz_tabs: <ChartIcon fontSize="small" />,
  calendar_heatmap: <CalendarIcon fontSize="small" />,
  gauge: <ChartIcon fontSize="small" />,
  indicator: <DataIcon fontSize="small" />,
  data_table: <GridIcon fontSize="small" />,
  device_list: <GridIcon fontSize="small" />,
  activity_timeline: <CalendarIcon fontSize="small" />,
  live_status: <DataIcon fontSize="small" />,
  geofence: <MapIcon fontSize="small" />,
  spatial_analytics: <MapIcon fontSize="small" />,
};

const WIDGET_DEFAULT_TITLES: Partial<Record<DashboardWidgetType, string>> = {
  web_map: 'Web Map',
  chart_bar: 'Bar Chart',
  chart_line: 'Line Chart',
  chart_area: 'Area Chart',
  chart_donut: 'Donut Chart',
  chart_radar: 'Radar Chart',
  chart_scatter: 'Scatter Plot',
  chart_treemap: 'Treemap',
  viz_tabs: 'Chart Tabs',
  calendar_heatmap: 'Calendar Heatmap',
  gauge: 'Gauge',
  indicator: 'Indicator',
  data_table: 'Data Table',
  device_list: 'Device List',
  activity_timeline: 'Activity Timeline',
  live_status: 'Live Status',
  geofence: 'Geofence',
  spatial_analytics: 'Spatial Analytics',
  placeholder: 'Placeholder',
  iframe: 'Embed',
  text: 'Text',
  widget_stack: 'Widget Stack',
  widget_group: 'Widget Group',
};

const DEFAULT_CONTENT_SCALE = 0.75;

function settingsTabsForWidget(widgetType: DashboardWidgetType): SettingsTabId[] {
  switch (widgetType) {
    case 'placeholder':
      return ['general', 'appearance', 'actions'];
    case 'iframe':
      return ['general', 'appearance', 'actions'];
    case 'text':
      return ['general', 'content', 'appearance', 'actions'];
    case 'chart_donut':
    case 'chart_radar':
    case 'chart_treemap':
    case 'viz_tabs':
      return ['general', 'data', 'chart', 'slices', 'appearance', 'actions'];
    case 'chart_bar':
    case 'chart_area':
      return ['general', 'data', 'chart', 'slices', 'appearance', 'actions'];
    case 'chart_scatter':
      return ['general', 'data', 'chart', 'slices', 'appearance', 'actions'];
    case 'chart_line':
    case 'calendar_heatmap':
      return ['general', 'data', 'chart', 'slices', 'appearance', 'actions'];
    case 'gauge':
      return ['general', 'data', 'chart', 'slices', 'appearance', 'actions'];
    case 'indicator':
      return ['general', 'data', 'indicator', 'appearance', 'actions'];
    case 'data_table':
      return ['general', 'data', 'columns', 'appearance', 'actions'];
    case 'device_list':
      return ['general', 'data', 'list', 'appearance', 'actions'];
    case 'activity_timeline':
    case 'live_status':
      return ['general', 'data', 'appearance', 'actions'];
    case 'web_map':
      return ['general', 'appearance', 'actions'];
    case 'spatial_analytics':
      return ['general', 'data', 'appearance', 'actions'];
    case 'geofence':
      return ['general', 'appearance', 'actions'];
    case 'widget_stack':
      return ['general', 'stack', 'appearance', 'actions'];
    case 'widget_group':
      return ['general', 'group', 'appearance', 'actions'];
    default:
      return ['general', 'appearance', 'actions'];
  }
}

export interface WidgetSettingsDialogProps {
  open: boolean;
  widgetType: DashboardWidgetType;
  initial: DashboardWidgetInstance | null;
  /** The layout ID of the widget being edited. */
  widgetId?: string;
  onClose: () => void;
  onSave: (config: DashboardWidgetInstance['config']) => void;
  /** All widgets — needed by StackSettingsPanel / GroupSettingsPanel / Actions tab. */
  allWidgets?: Record<string, DashboardWidgetInstance>;
  /** Called when a stack child is removed via the settings dialog. */
  onUnstackChild?: (childId: string) => void;
  /** Called when a group child is removed via the settings dialog. */
  onUngroupChild?: (childId: string) => void;
}

const WidgetSettingsDialog: React.FC<WidgetSettingsDialogProps> = ({
  open,
  widgetType,
  initial,
  widgetId,
  onClose,
  onSave,
  allWidgets,
  onUnstackChild,
  onUngroupChild,
}) => {
  const [customTitle, setCustomTitle] = useState('');
  const [textTitle, setTextTitle] = useState('');
  const [textBody, setTextBody] = useState('');
  const [cardBackground, setCardBackground] = useState('');
  const [cardBorderColor, setCardBorderColor] = useState('');
  const [cardBorderRadius, setCardBorderRadius] = useState<number>(4);
  const [cardOpacity, setCardOpacity] = useState<number>(1);
  const [titleColor, setTitleColor] = useState('');
  const [titleFontSize, setTitleFontSize] = useState<'small' | 'medium' | 'large'>('small');
  const [contentTextColor, setContentTextColor] = useState('');
  const [contentScale, setContentScale] = useState(DEFAULT_CONTENT_SCALE);
  const [hideHeader, setHideHeader] = useState(false);
  const [hideLabel, setHideLabel] = useState(false);
  const [vizTabs, setVizTabs] = useState<DashboardTabDefinition[]>(DEFAULT_VIZ_TABS);
  const [chartTitle, setChartTitle] = useState('');
  const [heatmapWeeks, setHeatmapWeeks] = useState(6);
  const [heatmapCellSizeInput, setHeatmapCellSizeInput] = useState('');
  const [settingsTab, setSettingsTab] = useState(0);
  const [donutInnerRadiusPercent, setDonutInnerRadiusPercent] = useState(55);
  const [donutStartAngle, setDonutStartAngle] = useState(90);
  const [legendMode, setLegendMode] = useState<ChartLegendMode>('percent');
  const [legendPlacement, setLegendPlacement] = useState<ChartLegendPlacement>('side');
  const [chartLabelFontSize, setChartLabelFontSize] = useState(11);
  const [chartTextColor, setChartTextColor] = useState('');
  const [chartAxisLabelMode, setChartAxisLabelMode] = useState<ChartAxisLabelMode>('auto');
  const [chartColorPaletteId, setChartColorPaletteId] = useState<ChartColorPaletteId>('default');
  const [indicatorFilterField, setIndicatorFilterField] = useState<string>('');
  const [indicatorFilterOperator, setIndicatorFilterOperator] = useState<IndicatorFilterOperator>('eq');
  const [indicatorFilterValue, setIndicatorFilterValue] = useState('');
  const [indicatorTextTop, setIndicatorTextTop] = useState('');
  const [indicatorTextMiddle, setIndicatorTextMiddle] = useState('Count: {value}');
  const [indicatorTextBottom, setIndicatorTextBottom] = useState('');
  const [indicatorColorTop, setIndicatorColorTop] = useState('');
  const [indicatorColorMiddle, setIndicatorColorMiddle] = useState('');
  const [indicatorColorBottom, setIndicatorColorBottom] = useState('');
  const [indicatorAccent, setIndicatorAccent] = useState('');
  const [indicatorFaceBg, setIndicatorFaceBg] = useState('');
  const [indicatorBgMiddle, setIndicatorBgMiddle] = useState('');
  const [indicatorShowLastUpdated, setIndicatorShowLastUpdated] = useState(true);

  const [boundMapWidgetId, setBoundMapWidgetId] = useState('');
  const [boundLayerId, setBoundLayerId] = useState('');
  const [bindingField, setBindingField] = useState('');
  const [bindingGroupByField, setBindingGroupByField] = useState('');
  const [bindingValueField, setBindingValueField] = useState('');
  const [bindingAggregation, setBindingAggregation] = useState('count');
  const [spatialInstructions, setSpatialInstructions] = useState('');
  const [iframeUrl, setIframeUrl] = useState('');
  const [iframeAllow, setIframeAllow] = useState('');
  const [gaugeMin, setGaugeMin] = useState(0);
  const [gaugeMax, setGaugeMax] = useState(100);
  const [gaugeLabel, setGaugeLabel] = useState('');
  const [gaugeColor, setGaugeColor] = useState('');
  const [tablePageSize, setTablePageSize] = useState(25);
  const [tableVisibleColumns, setTableVisibleColumns] = useState<string[]>([]);
  const [tableColumnLabels, setTableColumnLabels] = useState<Record<string, string>>({});
  const [scatterXField, setScatterXField] = useState('');
  const [scatterYField, setScatterYField] = useState('');
  const [stackNavStyle, setStackNavStyle] = useState<WidgetStackNavStyle>('tabs');
  const [stackChildIds, setStackChildIds] = useState<string[]>([]);
  const [stackTabLabels, setStackTabLabels] = useState<Record<string, string>>({});
  const [groupDirection, setGroupDirection] = useState<WidgetGroupDirection>('row');
  const [groupChildIds, setGroupChildIds] = useState<string[]>([]);
  const [geofenceDefaultColor, setGeofenceDefaultColor] = useState('#f44336');

  const [listLineTemplate, setListLineTemplate] = useState('');
  const [listIconMode, setListIconMode] = useState<'none' | 'symbol'>('none');
  const [listHighlightColor, setListHighlightColor] = useState('#ffeb3b');
  const [listSelectionMode, setListSelectionMode] = useState<'single' | 'multiple'>('single');
  const [listActions, setListActions] = useState<Array<{ type: string; enabled: boolean }>>([
    { type: 'filter', enabled: false },
    { type: 'flash', enabled: false },
    { type: 'pan', enabled: false },
    { type: 'zoom', enabled: false },
    { type: 'popup', enabled: false },
    { type: 'follow', enabled: false },
    { type: 'streetview', enabled: false },
  ]);
  const [listShowLastOnly, setListShowLastOnly] = useState(true);
  const [listFormatRules, setListFormatRules] = useState<DeviceFieldFormatRule[]>([]);
  const [timelineOrientation, setTimelineOrientation] = useState<'vertical' | 'horizontal'>('vertical');
  const [timelineViewMode, setTimelineViewMode] = useState<'timeline' | 'chart'>('timeline');
  const [timelineColorPalette, setTimelineColorPalette] = useState<string>('default');

  const [crossFilterTargets, setCrossFilterTargets] = useState<CrossFilterTarget[]>([]);

  const webMapCtx = useWebMapContext();

  const boundLayerFields = useMemo(() => {
    if (!webMapCtx || !boundLayerId) return [];
    const features = webMapCtx.getFeaturesForLayer(boundLayerId);
    const keySet = new Set<string>();

    const addKeys = (obj: Record<string, unknown>, prefix: string) => {
      for (const k of Object.keys(obj)) {
        if (k.startsWith('__') || k === 'geometry') continue;
        const path = prefix ? `${prefix}.${k}` : k;
        keySet.add(path);
        let val = obj[k];
        if (typeof val === 'string' && val.startsWith('{')) {
          try { val = JSON.parse(val); } catch { /* not JSON */ }
        }
        if (val && typeof val === 'object' && !Array.isArray(val)) {
          addKeys(val as Record<string, unknown>, path);
        }
      }
    };

    for (const f of features.slice(0, 100)) {
      if (f.properties) addKeys(f.properties as Record<string, unknown>, '');
    }
    return Array.from(keySet).sort((a, b) => {
      const da = a.includes('.') ? 1 : 0;
      const db = b.includes('.') ? 1 : 0;
      if (da !== db) return da - db;
      return a.localeCompare(b);
    });
  }, [webMapCtx, boundLayerId]);

  const fieldValuesMap = useMemo<Record<string, string[]>>(() => {
    if (!webMapCtx || !boundLayerId) return {};
    const features = webMapCtx.getFeaturesForLayer(boundLayerId);
    const map: Record<string, Set<string>> = {};

    const resolve = (props: Record<string, unknown>, path: string): unknown => {
      const parts = path.split('.');
      let cur: unknown = props;
      for (const part of parts) {
        if (cur == null || typeof cur !== 'object') return undefined;
        let val = (cur as Record<string, unknown>)[part];
        if (typeof val === 'string' && val.startsWith('{')) {
          try { val = JSON.parse(val); } catch { /* not JSON */ }
        }
        cur = val;
      }
      return cur;
    };

    for (const field of boundLayerFields) {
      const vals = new Set<string>();
      for (const f of features.slice(0, 500)) {
        if (!f.properties) continue;
        const raw = resolve(f.properties as Record<string, unknown>, field);
        if (raw != null) vals.add(String(raw));
      }
      if (vals.size > 0 && vals.size <= 200) {
        map[field] = vals;
      }
    }

    const result: Record<string, string[]> = {};
    for (const [k, v] of Object.entries(map)) {
      result[k] = Array.from(v).sort((a, b) => a.localeCompare(b));
    }
    return result;
  }, [webMapCtx, boundLayerId, boundLayerFields]);

  const visibleTabs = useMemo(() => settingsTabsForWidget(widgetType), [widgetType]);

  const otherWidgets = useMemo(() => {
    if (!allWidgets) return [];
    return (Object.entries(allWidgets) as [string, DashboardWidgetInstance][])
      .filter(([id]) => id !== widgetId)
      .map(([id, inst]) => ({
        id,
        type: inst.type,
        title: inst.config?.customTitle?.trim() || WIDGET_DEFAULT_TITLES[inst.type] || id,
      }));
  }, [allWidgets, widgetId]);

  useEffect(() => {
    if (open) setSettingsTab(0);
  }, [open, widgetType]);

  useEffect(() => {
    setSettingsTab((i) => Math.min(i, Math.max(0, visibleTabs.length - 1)));
  }, [visibleTabs.length]);

  useEffect(() => {
    if (!open || !initial) return;
    const c = initial.config ?? {};
    setCustomTitle(c.customTitle ?? '');
    setTextTitle(c.title ?? '');
    setTextBody(c.body ?? '');
    setCardBackground(c.style?.cardBackground ?? '');
    setCardBorderColor(c.style?.cardBorderColor ?? '');
    setCardBorderRadius(c.style?.cardBorderRadius ?? 4);
    setCardOpacity(c.style?.cardOpacity ?? 1);
    setTitleColor(c.style?.titleColor ?? '');
    setTitleFontSize(c.style?.titleFontSize ?? 'small');
    setContentTextColor(c.style?.contentTextColor ?? '');
    setContentScale(
      typeof c.style?.contentScale === 'number' && !Number.isNaN(c.style.contentScale)
        ? c.style.contentScale
        : DEFAULT_CONTENT_SCALE
    );
    setHideHeader(c.style?.hideHeader ?? false);
    setHideLabel(c.style?.hideLabel ?? false);
    setVizTabs(c.tabs?.length ? c.tabs : DEFAULT_VIZ_TABS);
    setChartTitle(c.chartTitle ?? '');
    setHeatmapWeeks(c.heatmapWeeks ?? 6);
    setHeatmapCellSizeInput(
      typeof c.heatmapCellSizePx === 'number' && !Number.isNaN(c.heatmapCellSizePx)
        ? String(c.heatmapCellSizePx)
        : ''
    );
    setDonutInnerRadiusPercent(
      typeof c.donutInnerRadiusPercent === 'number' ? c.donutInnerRadiusPercent : 55
    );
    setDonutStartAngle(typeof c.donutStartAngle === 'number' ? c.donutStartAngle : 90);
    setLegendMode(c.legendMode ?? 'percent');
    setLegendPlacement(c.legendPlacement ?? 'side');
    setChartLabelFontSize(typeof c.chartLabelFontSize === 'number' ? c.chartLabelFontSize : 11);
    setChartTextColor(c.chartTextColor ?? '');
    setChartAxisLabelMode(c.chartAxisLabelMode ?? 'auto');
    setChartColorPaletteId((c.chartColorPaletteId as ChartColorPaletteId) ?? 'default');
    setIndicatorFilterField((c.indicatorFilterField as string) ?? '');
    setIndicatorFilterOperator((c.indicatorFilterOperator as IndicatorFilterOperator) ?? 'eq');
    setIndicatorFilterValue(c.indicatorFilterValue ?? '');
    setIndicatorTextTop(c.indicatorTextTop ?? '');
    setIndicatorTextMiddle(c.indicatorTextMiddle ?? 'Count: {value}');
    setIndicatorTextBottom(c.indicatorTextBottom ?? '');
    setIndicatorColorTop(c.indicatorColorTop ?? '');
    setIndicatorColorMiddle(c.indicatorColorMiddle ?? '');
    setIndicatorColorBottom(c.indicatorColorBottom ?? '');
    setIndicatorAccent(c.indicatorAccent ?? '');
    setIndicatorFaceBg(c.indicatorFaceBg ?? '');
    setIndicatorBgMiddle(c.indicatorBgMiddle ?? '');
    setIndicatorShowLastUpdated(c.indicatorShowLastUpdated !== false);
    const db = c.dataBinding;
    setBoundMapWidgetId(db?.boundMapWidgetId ?? '');
    setBoundLayerId(db?.boundLayerId ?? '');
    setBindingField(db?.field ?? '');
    setBindingGroupByField(db?.groupByField ?? '');
    setBindingValueField(db?.valueField ?? '');
    setBindingAggregation(db?.aggregation ?? 'count');
    setSpatialInstructions(c.spatialInstructions ?? '');
    setIframeUrl(c.iframeUrl ?? '');
    setIframeAllow(c.iframeAllow ?? '');
    setGaugeMin(typeof c.gaugeMin === 'number' ? c.gaugeMin : 0);
    setGaugeMax(typeof c.gaugeMax === 'number' ? c.gaugeMax : 100);
    setGaugeLabel(c.gaugeLabel ?? '');
    setGaugeColor(c.gaugeColor ?? '');
    setTablePageSize(typeof c.tablePageSize === 'number' ? c.tablePageSize : 25);
    setTableVisibleColumns(Array.isArray(c.tableVisibleColumns) ? c.tableVisibleColumns : []);
    setTableColumnLabels(c.tableColumnLabels && typeof c.tableColumnLabels === 'object' ? { ...c.tableColumnLabels } : {});
    setScatterXField(c.scatterXField ?? '');
    setScatterYField(c.scatterYField ?? '');
    setStackNavStyle((c.navStyle as WidgetStackNavStyle) ?? 'tabs');
    setStackChildIds(Array.isArray(c.childIds) ? c.childIds : []);
    setStackTabLabels(c.tabLabels && typeof c.tabLabels === 'object' ? { ...c.tabLabels } : {});
    setGroupDirection((c.direction as WidgetGroupDirection) ?? 'row');
    setGroupChildIds(Array.isArray(c.groupChildIds) ? c.groupChildIds : []);
    setGeofenceDefaultColor((c as any).geofenceDefaultColor ?? '#f44336');
    setListLineTemplate(c.lineTemplate ?? '');
    setListIconMode(c.iconMode ?? 'none');
    setListHighlightColor(c.highlightColor ?? '#ffeb3b');
    setListSelectionMode(c.selectionMode ?? 'single');
    setListShowLastOnly(c.uniqueField != null && c.uniqueField !== '');
    setListFormatRules(Array.isArray(c.formatRules) ? c.formatRules : []);
    setTimelineOrientation(c.orientation === 'horizontal' ? 'horizontal' : 'vertical');
    setTimelineViewMode(c.viewMode === 'chart' ? 'chart' : 'timeline');
    setTimelineColorPalette(c.colorPalette ?? 'default');
    setCrossFilterTargets(Array.isArray((c as any).crossFilterTargets) ? (c as any).crossFilterTargets : []);
    const defaultActions = [
      { type: 'filter', enabled: false },
      { type: 'flash', enabled: false },
      { type: 'pan', enabled: false },
      { type: 'zoom', enabled: false },
      { type: 'popup', enabled: false },
      { type: 'follow', enabled: false },
      { type: 'streetview', enabled: false },
    ];
    if (Array.isArray(c.actions)) {
      for (const da of defaultActions) {
        const existing = (c.actions as Array<{ type: string; enabled: boolean }>).find(
          (a) => a.type === da.type,
        );
        if (existing) da.enabled = existing.enabled;
      }
    }
    setListActions(defaultActions);
  }, [open, initial]);

  const handleSave = () => {
    const prev = initial?.config ?? {};

    const style: DashboardWidgetStyle = {};
    if (cardBackground.trim()) style.cardBackground = cardBackground.trim();
    if (cardBorderColor.trim()) style.cardBorderColor = cardBorderColor.trim();
    if (cardBorderRadius !== 4) style.cardBorderRadius = cardBorderRadius;
    if (cardOpacity !== 1) style.cardOpacity = cardOpacity;
    if (titleColor.trim()) style.titleColor = titleColor.trim();
    if (titleFontSize !== 'small') style.titleFontSize = titleFontSize;
    if (contentTextColor.trim()) style.contentTextColor = contentTextColor.trim();
    if (Math.abs(contentScale - DEFAULT_CONTENT_SCALE) > 0.01) style.contentScale = Math.round(contentScale * 100) / 100;
    if (hideHeader) style.hideHeader = true;
    if (hideLabel) style.hideLabel = true;
    const hasStyle = Object.keys(style).length > 0;

    const next = {
      ...prev,
      customTitle: customTitle.trim() || undefined,
    } as NonNullable<DashboardWidgetInstance['config']>;

    if (widgetType === 'text') {
      next.title = textTitle.trim() || 'Text';
      next.body = textBody;
    }

    if (widgetType === 'viz_tabs') {
      next.tabs = vizTabs.length ? vizTabs : DEFAULT_VIZ_TABS;
      next.heatmapWeeks = heatmapWeeks;
    }

    if (isVizWidgetType(widgetType) || widgetType === 'gauge') {
      next.chartTitle = chartTitle.trim() || undefined;
      if (widgetType === 'calendar_heatmap') {
        next.heatmapWeeks = heatmapWeeks;
      }
    }

    delete (next as Record<string, unknown>).groupBy;

    const chartish = isVizWidgetType(widgetType) || widgetType === 'gauge';
    const chartVisualKeys = [
      'chartColorPaletteId',
      'chartLabelFontSize',
      'chartTextColor',
      'chartAxisLabelMode',
      'donutInnerRadiusPercent',
      'donutStartAngle',
      'legendMode',
      'legendPlacement',
    ] as const;
    if (!chartish) {
      chartVisualKeys.forEach((k) => {
        delete (next as Record<string, unknown>)[k];
      });
    } else {
      if (chartLabelFontSize !== 11) next.chartLabelFontSize = chartLabelFontSize;
      else delete next.chartLabelFontSize;
      if (chartTextColor.trim()) next.chartTextColor = chartTextColor.trim();
      else delete next.chartTextColor;

      const usesAxisLabels =
        widgetType === 'chart_bar'
        || widgetType === 'chart_line'
        || widgetType === 'chart_area'
        || widgetType === 'viz_tabs';
      if (usesAxisLabels && chartAxisLabelMode !== 'auto') {
        next.chartAxisLabelMode = chartAxisLabelMode;
      } else {
        delete next.chartAxisLabelMode;
      }

      const usesPalette =
        widgetType === 'chart_bar' || widgetType === 'chart_area' || widgetType === 'chart_donut'
        || widgetType === 'chart_radar' || widgetType === 'chart_scatter' || widgetType === 'chart_treemap'
        || widgetType === 'chart_line' || widgetType === 'calendar_heatmap'
        || widgetType === 'gauge' || widgetType === 'viz_tabs';
      if (usesPalette) {
        if (chartColorPaletteId !== 'default') next.chartColorPaletteId = chartColorPaletteId;
        else delete next.chartColorPaletteId;
      } else {
        delete next.chartColorPaletteId;
      }

      const usesDonut = widgetType === 'chart_donut' || widgetType === 'chart_radar' || widgetType === 'viz_tabs';
      if (usesDonut) {
        next.donutInnerRadiusPercent = donutInnerRadiusPercent;
        next.donutStartAngle = donutStartAngle;
        next.legendMode = legendMode;
        next.legendPlacement = legendPlacement;
      } else {
        delete next.donutInnerRadiusPercent;
        delete next.donutStartAngle;
        delete next.legendMode;
        delete next.legendPlacement;
      }
    }

    if (widgetType === 'spatial_analytics') {
      next.spatialInstructions = spatialInstructions.trim() || undefined;
    } else {
      delete (next as Record<string, unknown>).spatialInstructions;
    }

    if (widgetType === 'iframe') {
      next.iframeUrl = iframeUrl.trim() || undefined;
      next.iframeAllow = iframeAllow.trim() || undefined;
    } else {
      delete (next as Record<string, unknown>).iframeUrl;
      delete (next as Record<string, unknown>).iframeAllow;
    }

    if (widgetType === 'gauge') {
      next.gaugeMin = gaugeMin;
      next.gaugeMax = gaugeMax;
      next.gaugeLabel = gaugeLabel.trim() || undefined;
      next.gaugeColor = gaugeColor.trim() || undefined;
    } else {
      delete (next as Record<string, unknown>).gaugeMin;
      delete (next as Record<string, unknown>).gaugeMax;
      delete (next as Record<string, unknown>).gaugeLabel;
      delete (next as Record<string, unknown>).gaugeColor;
    }

    if (widgetType === 'data_table') {
      next.tablePageSize = tablePageSize;
      next.tableVisibleColumns = tableVisibleColumns.length > 0 ? tableVisibleColumns : undefined;
      const cleanedLabels: Record<string, string> = {};
      for (const [k, v] of Object.entries(tableColumnLabels)) {
        if (typeof v === 'string' && v.trim()) cleanedLabels[k] = v.trim();
      }
      next.tableColumnLabels = Object.keys(cleanedLabels).length > 0 ? cleanedLabels : undefined;
    } else {
      delete (next as Record<string, unknown>).tablePageSize;
      delete (next as Record<string, unknown>).tableCompact;
      delete (next as Record<string, unknown>).tableVisibleColumns;
      delete (next as Record<string, unknown>).tableColumnLabels;
    }

    if (widgetType === 'chart_scatter') {
      next.scatterXField = scatterXField || undefined;
      next.scatterYField = scatterYField || undefined;
    } else {
      delete (next as Record<string, unknown>).scatterXField;
      delete (next as Record<string, unknown>).scatterYField;
    }

    next.style = hasStyle ? style : undefined;

    if (boundMapWidgetId) {
      const binding: WidgetDataBinding = { boundMapWidgetId };
      if (boundLayerId) binding.boundLayerId = boundLayerId;
      if (bindingField) binding.field = bindingField;
      if (bindingGroupByField) binding.groupByField = bindingGroupByField;
      if (bindingValueField) binding.valueField = bindingValueField;
      if (bindingAggregation && bindingAggregation !== 'count') binding.aggregation = bindingAggregation as WidgetDataBinding['aggregation'];
      else if (bindingAggregation === 'count') binding.aggregation = 'count';
      next.dataBinding = binding;
    } else {
      delete (next as Record<string, unknown>).dataBinding;
    }

    const heatmapCellTrim = heatmapCellSizeInput.trim();
    let heatmapCellParsed: number | undefined;
    if (heatmapCellTrim) {
      const n = Math.round(Number(heatmapCellTrim));
      if (Number.isFinite(n)) heatmapCellParsed = Math.min(96, Math.max(4, n));
    }
    if (widgetType === 'viz_tabs' || widgetType === 'calendar_heatmap') {
      if (heatmapCellParsed != null) next.heatmapCellSizePx = heatmapCellParsed;
      else delete next.heatmapCellSizePx;
    } else {
      delete next.heatmapCellSizePx;
    }

    if (widgetType === 'indicator') {
      next.indicatorFilterField = indicatorFilterField;
      next.indicatorFilterOperator = indicatorFilterOperator;
      next.indicatorFilterValue = indicatorFilterValue.trim() || undefined;
      next.indicatorTextTop = indicatorTextTop.trim() || undefined;
      next.indicatorTextMiddle = indicatorTextMiddle.trim() || undefined;
      next.indicatorTextBottom = indicatorTextBottom.trim() || undefined;
      next.indicatorColorTop = indicatorColorTop.trim() || undefined;
      next.indicatorColorMiddle = indicatorColorMiddle.trim() || undefined;
      next.indicatorColorBottom = indicatorColorBottom.trim() || undefined;
      next.indicatorAccent = indicatorAccent.trim() || undefined;
      next.indicatorFaceBg = indicatorFaceBg.trim() || undefined;
      next.indicatorBgMiddle = indicatorBgMiddle.trim() || undefined;
      next.indicatorShowLastUpdated = indicatorShowLastUpdated;
    } else {
      INDICATOR_CONFIG_KEYS.forEach((k) => {
        delete (next as Record<string, unknown>)[k];
      });
    }

    if (widgetType === 'widget_stack') {
      next.navStyle = stackNavStyle;
      next.childIds = stackChildIds;
      const cleanLabels: Record<string, string> = {};
      for (const [k, v] of Object.entries(stackTabLabels)) {
        if (typeof v === 'string' && v.trim()) cleanLabels[k] = v;
      }
      next.tabLabels = Object.keys(cleanLabels).length > 0 ? cleanLabels : undefined;
    } else {
      delete (next as Record<string, unknown>).navStyle;
      delete (next as Record<string, unknown>).childIds;
      delete (next as Record<string, unknown>).activeChildIndex;
      delete (next as Record<string, unknown>).tabLabels;
    }

    if (widgetType === 'widget_group') {
      next.groupChildIds = groupChildIds;
      next.direction = groupDirection;
    } else {
      delete (next as Record<string, unknown>).groupChildIds;
      delete (next as Record<string, unknown>).direction;
    }

    if (widgetType === 'device_list') {
      next.lineTemplate = listLineTemplate.trim() || undefined;
      next.iconMode = listIconMode;
      next.highlightColor = listHighlightColor;
      next.selectionMode = listSelectionMode;
      next.actions = listActions as any;
      next.uniqueField = listShowLastOnly ? (next.nameField || 'entity_id') : undefined;
      next.formatRules = listFormatRules.length > 0 ? listFormatRules : undefined;
    }

    if (widgetType === 'activity_timeline') {
      next.orientation = timelineOrientation;
      next.viewMode = timelineViewMode;
      next.colorPalette = timelineColorPalette as any;
    }

    if (widgetType === 'geofence') {
      (next as any).geofenceDefaultColor = geofenceDefaultColor.trim() || '#f44336';
    }

    const enabledTargets = crossFilterTargets.filter((t) => t.enabled);
    (next as any).crossFilterTargets = enabledTargets.length > 0 ? enabledTargets : undefined;

    onSave(next);
    onClose();
  };

  const tabIndex = Math.min(settingsTab, Math.max(0, visibleTabs.length - 1));
  const tabId = visibleTabs[tabIndex];

  const dataBindingPanelProps = {
    boundMapWidgetId,
    setBoundMapWidgetId,
    boundLayerId,
    setBoundLayerId,
    field: bindingField,
    setField: setBindingField,
    groupByField: bindingGroupByField,
    setGroupByField: setBindingGroupByField,
    valueField: bindingValueField,
    setValueField: setBindingValueField,
    aggregation: bindingAggregation,
    setAggregation: setBindingAggregation,
    webMapWidgets:
      webMapCtx && webMapCtx.layers.length > 0
        ? [{ id: '_webmap_ctx', title: 'Web Map (current dashboard)' }]
        : [],
    availableLayers:
      webMapCtx
        ? webMapCtx.layers.map((l: WebMapLayerConfig) => ({
            id: l.id,
            label: l.sourceName || `${l.schema}.${l.table}`,
          }))
        : [],
    availableFields:
      webMapCtx && boundLayerId
        ? (webMapCtx.layerSchemas.get(boundLayerId) ?? []).map((c) => ({ name: c.name, type: c.type }))
        : [],
    widgetType,
  };

  const getTargetFields = (targetWidgetId: string): Array<{ name: string; type: string }> => {
    if (!webMapCtx || !allWidgets) return [];
    const targetWidget = allWidgets[targetWidgetId];
    if (!targetWidget) return [];
    const targetLayerId = targetWidget.config?.dataBinding?.boundLayerId;
    if (!targetLayerId) return [];
    return (webMapCtx.layerSchemas.get(targetLayerId) ?? []).map((c) => ({ name: c.name, type: c.type }));
  };

  const getFieldTypeIcon = (type: string): React.ReactNode => {
    const t = type.toLowerCase();
    if (t.includes('timestamp') || t.includes('date') || t.includes('time')) return <CalendarIcon sx={{ fontSize: 16 }} />;
    if (t.includes('int') || t.includes('numeric') || t.includes('real') || t.includes('double') || t.includes('float')) return <TagIcon sx={{ fontSize: 16 }} />;
    return <Typography variant="caption" sx={{ fontSize: 10, lineHeight: 1 }}>abc</Typography>;
  };

  const updateCrossFilterTarget = (targetId: string, patch: Partial<CrossFilterTarget>) => {
    setCrossFilterTargets((prev) => {
      const existing = prev.find((t) => t.widgetId === targetId);
      if (existing) {
        return prev.map((t) => t.widgetId === targetId ? { ...t, ...patch } : t);
      }
      return [...prev, { widgetId: targetId, enabled: false, fieldMappings: [], ...patch }];
    });
  };

  const updateFieldMapping = (targetId: string, mappingIdx: number, patch: Partial<CrossFilterFieldMapping>) => {
    setCrossFilterTargets((prev) =>
      prev.map((t) => {
        if (t.widgetId !== targetId) return t;
        const mappings = [...t.fieldMappings];
        mappings[mappingIdx] = { ...mappings[mappingIdx], ...patch };
        return { ...t, fieldMappings: mappings };
      })
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Widget settings</DialogTitle>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 0.5 }}>
        <Tabs
          value={tabIndex}
          onChange={(_, v) => setSettingsTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          aria-label="Widget settings categories"
        >
          {visibleTabs.map((t) => (
            <Tab key={t} label={TAB_LABEL[t]} />
          ))}
        </Tabs>
      </Box>
      <DialogContent sx={{ minHeight: 280 }}>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {tabId === 'general' && (
            <>
              <TextField
                label="Panel title (header)"
                fullWidth
                size="small"
                value={customTitle}
                onChange={(e) => setCustomTitle(e.target.value)}
                helperText="Leave empty to use the default widget name."
              />
              {widgetType === 'text' && (
                <TextField
                  label="Content title"
                  fullWidth
                  size="small"
                  value={textTitle}
                  onChange={(e) => setTextTitle(e.target.value)}
                />
              )}
              {widgetType === 'placeholder' && (
                <Typography variant="body2" color="text.secondary">
                  Placeholder tiles reserve space on the layout. Replace them with a chart or analytics widget
                  from the Add widget menu.
                </Typography>
              )}
              {widgetType === 'activity_timeline' && (
                <Stack spacing={2}>
                  <SettingsToggleGroup
                    label="View mode"
                    value={timelineViewMode}
                    onChange={(v) => setTimelineViewMode(v as 'timeline' | 'chart')}
                    options={[
                      { value: 'timeline', label: 'Timeline' },
                      { value: 'chart', label: 'Chart' },
                    ]}
                  />
                  {timelineViewMode === 'timeline' && (
                    <SettingsToggleGroup
                      label="Layout orientation"
                      value={timelineOrientation}
                      onChange={(v) => setTimelineOrientation(v as 'vertical' | 'horizontal')}
                      options={[
                        { value: 'vertical', label: 'Vertical' },
                        { value: 'horizontal', label: 'Horizontal' },
                      ]}
                    />
                  )}
                  <SettingsPalettePicker
                    label="Color palette"
                    value={timelineColorPalette as ChartColorPaletteId}
                    onChange={(v) => setTimelineColorPalette(v)}
                  />
                </Stack>
              )}
              {widgetType === 'iframe' && (
                <>
                  <TextField
                    label="Embed URL"
                    fullWidth
                    size="small"
                    value={iframeUrl}
                    onChange={(e) => setIframeUrl(e.target.value)}
                    placeholder="https://example.com/embed"
                    helperText="The URL to embed inside this widget."
                  />
                  <TextField
                    label="Allow permissions (optional)"
                    fullWidth
                    size="small"
                    value={iframeAllow}
                    onChange={(e) => setIframeAllow(e.target.value)}
                    placeholder="autoplay; fullscreen"
                    helperText="Iframe allow attribute (e.g. 'autoplay; fullscreen')."
                  />
                </>
              )}
              {widgetType === 'gauge' && (
                <Stack spacing={2}>
                  <TextField
                    label="Gauge label"
                    fullWidth
                    size="small"
                    value={gaugeLabel}
                    onChange={(e) => setGaugeLabel(e.target.value)}
                  />
                  <Stack direction="row" spacing={2}>
                    <TextField
                      label="Min"
                      type="number"
                      size="small"
                      value={gaugeMin}
                      onChange={(e) => setGaugeMin(Number(e.target.value) || 0)}
                      sx={{ flex: 1 }}
                    />
                    <TextField
                      label="Max"
                      type="number"
                      size="small"
                      value={gaugeMax}
                      onChange={(e) => setGaugeMax(Number(e.target.value) || 100)}
                      sx={{ flex: 1 }}
                    />
                  </Stack>
                  <SettingsColorPicker
                    label="Gauge color"
                    value={gaugeColor}
                    onChange={setGaugeColor}
                    placeholder="#00e5ff"
                  />
                </Stack>
              )}
              {widgetType === 'data_table' && (
                <TextField
                  label="Rows per page"
                  type="number"
                  size="small"
                  inputProps={{ min: 5, max: 100 }}
                  value={tablePageSize}
                  onChange={(e) => setTablePageSize(Math.min(100, Math.max(5, Number(e.target.value) || 25)))}
                />
              )}
              {widgetType === 'geofence' && (
                <Stack spacing={2}>
                  <Typography variant="body2" color="text.secondary">
                    Geofence zones are drawn on the bound Web Map. Set the default color for new fences below.
                  </Typography>
                  <SettingsColorPicker
                    label="Default fence color"
                    value={geofenceDefaultColor}
                    onChange={setGeofenceDefaultColor}
                    placeholder="#f44336"
                  />
                </Stack>
              )}
            </>
          )}

          {tabId === 'data' && widgetType === 'spatial_analytics' && (
            <Stack spacing={2}>
              <Typography variant="subtitle2">Web Map data binding</Typography>
              <Typography variant="body2" color="text.secondary">
                Connect this widget to a Web Map layer. The AI will analyse the features in the bound layer.
              </Typography>
              <DataBindingPanel {...dataBindingPanelProps} />
              <Typography variant="subtitle2" sx={{ mt: 2 }}>
                Custom AI context
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Provide extra context or instructions for the AI about this dataset (optional).
              </Typography>
              <TextField
                fullWidth
                multiline
                minRows={3}
                maxRows={8}
                size="small"
                placeholder="e.g. This layer contains crime incidents for 2024. The severity field ranges from 1 (minor) to 5 (critical)."
                value={spatialInstructions}
                onChange={(e) => setSpatialInstructions(e.target.value)}
              />
            </Stack>
          )}

          {tabId === 'data' && widgetType !== 'spatial_analytics' && (
            <Stack spacing={2}>
              <Typography variant="subtitle2">Data binding</Typography>
              <Typography variant="body2" color="text.secondary">
                Bind this widget to a Web Map layer to drive chart data or indicator values.
              </Typography>
              <DataBindingPanel {...dataBindingPanelProps} />
              {widgetType === 'chart_scatter' && boundLayerId && (
                <>
                  <Typography variant="subtitle2">Scatter axis fields</Typography>
                  <Stack direction="row" spacing={2}>
                    <TextField
                      label="X-axis field"
                      size="small"
                      fullWidth
                      value={scatterXField}
                      onChange={(e) => setScatterXField(e.target.value)}
                      helperText="Numeric field for horizontal axis"
                    />
                    <TextField
                      label="Y-axis field"
                      size="small"
                      fullWidth
                      value={scatterYField}
                      onChange={(e) => setScatterYField(e.target.value)}
                      helperText="Numeric field for vertical axis"
                    />
                  </Stack>
                </>
              )}
              <ChartWidgetDataPanel
                widgetType={widgetType}
                vizTabs={vizTabs}
                setVizTabs={setVizTabs}
                heatmapWeeks={heatmapWeeks}
                setHeatmapWeeks={setHeatmapWeeks}
                heatmapCellSizeInput={heatmapCellSizeInput}
                setHeatmapCellSizeInput={setHeatmapCellSizeInput}
              />
            </Stack>
          )}

          {tabId === 'chart' && (
            <ChartOptionsPanel
              widgetType={widgetType}
              chartTitle={chartTitle}
              setChartTitle={setChartTitle}
              donutInnerRadiusPercent={donutInnerRadiusPercent}
              setDonutInnerRadiusPercent={setDonutInnerRadiusPercent}
              donutStartAngle={donutStartAngle}
              setDonutStartAngle={setDonutStartAngle}
              legendMode={legendMode}
              setLegendMode={setLegendMode}
              legendPlacement={legendPlacement}
              setLegendPlacement={setLegendPlacement}
              chartLabelFontSize={chartLabelFontSize}
              setChartLabelFontSize={setChartLabelFontSize}
              chartTextColor={chartTextColor}
              setChartTextColor={setChartTextColor}
              chartAxisLabelMode={chartAxisLabelMode}
              setChartAxisLabelMode={setChartAxisLabelMode}
            />
          )}

          {tabId === 'slices' && (
            <SlicesPalettePanel
              chartColorPaletteId={chartColorPaletteId}
              setChartColorPaletteId={setChartColorPaletteId}
            />
          )}

          {tabId === 'content' && widgetType === 'text' && (
            <Stack spacing={2}>
              <Typography variant="subtitle2">Rich text content</Typography>
              <Typography variant="body2" color="text.secondary">
                Use the toolbar to format text, add links, images, and more. Toggle the code button to edit raw HTML.
              </Typography>
              <RichTextEditor
                value={textBody}
                onChange={setTextBody}
                placeholder="Enter formatted content…"
                minHeight={220}
              />
            </Stack>
          )}

          {tabId === 'columns' && widgetType === 'data_table' && (() => {
            const layerFeatures = boundLayerId && webMapCtx
              ? webMapCtx.getFeaturesForLayer(boundLayerId)
              : [];
            const allCols: string[] = [];
            const seen = new Set<string>();
            for (const f of layerFeatures.slice(0, 100)) {
              if (f.properties) {
                for (const k of Object.keys(f.properties)) {
                  if (!k.startsWith('__') && k !== 'geometry' && !seen.has(k)) {
                    seen.add(k);
                    allCols.push(k);
                  }
                }
              }
            }

            const hasExplicitCols = tableVisibleColumns.length > 0;
            const displayOrder = hasExplicitCols
              ? [...tableVisibleColumns, ...allCols.filter((c) => !tableVisibleColumns.includes(c))]
              : allCols;

            const isVisible = (col: string) =>
              !hasExplicitCols || tableVisibleColumns.includes(col);

            const toggleCol = (col: string) => {
              if (!hasExplicitCols) {
                setTableVisibleColumns(allCols.filter((c) => c !== col));
              } else if (tableVisibleColumns.includes(col)) {
                setTableVisibleColumns(tableVisibleColumns.filter((c) => c !== col));
              } else {
                setTableVisibleColumns([...tableVisibleColumns, col]);
              }
            };

            const moveCol = (col: string, direction: -1 | 1) => {
              const list = hasExplicitCols ? [...tableVisibleColumns] : [...allCols];
              const idx = list.indexOf(col);
              if (idx < 0) return;
              const swapIdx = idx + direction;
              if (swapIdx < 0 || swapIdx >= list.length) return;
              [list[idx], list[swapIdx]] = [list[swapIdx], list[idx]];
              setTableVisibleColumns(list);
            };

            return (
              <Stack spacing={2}>
                <Typography variant="subtitle2">Column visibility &amp; display names</Typography>
                <Typography variant="body2" color="text.secondary">
                  Toggle columns on/off, reorder them, and set custom display names.
                  {!allCols.length && ' Bind to a Web Map layer first to see available columns.'}
                </Typography>
                {allCols.length > 0 && (
                  <List dense disablePadding sx={{ maxHeight: 360, overflow: 'auto' }}>
                    {displayOrder.map((col) => {
                      const checked = isVisible(col);
                      const visIdx = hasExplicitCols ? tableVisibleColumns.indexOf(col) : -1;
                      return (
                        <ListItem
                          key={col}
                          disablePadding
                          sx={{
                            px: 0.5,
                            py: 0.25,
                            opacity: checked ? 1 : 0.45,
                            '&:hover': { bgcolor: 'action.hover' },
                          }}
                          secondaryAction={
                            checked && hasExplicitCols ? (
                              <Stack direction="row" spacing={0}>
                                <IconButton
                                  size="small"
                                  disabled={visIdx <= 0}
                                  onClick={() => moveCol(col, -1)}
                                >
                                  <MoveUpIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  disabled={visIdx >= tableVisibleColumns.length - 1}
                                  onClick={() => moveCol(col, 1)}
                                >
                                  <MoveDownIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Stack>
                            ) : null
                          }
                        >
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <Checkbox
                              edge="start"
                              size="small"
                              checked={checked}
                              onChange={() => toggleCol(col)}
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Stack direction="row" spacing={1} alignItems="center">
                                <DragIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                                <Typography variant="body2" sx={{ minWidth: 120, fontFamily: 'monospace', fontSize: 12 }}>
                                  {col}
                                </Typography>
                                <TextField
                                  size="small"
                                  variant="standard"
                                  placeholder={col}
                                  value={tableColumnLabels[col] ?? ''}
                                  onChange={(e) =>
                                    setTableColumnLabels((prev) => ({ ...prev, [col]: e.target.value }))
                                  }
                                  InputProps={{ sx: { fontSize: 12 } }}
                                  sx={{ flex: 1 }}
                                />
                              </Stack>
                            }
                          />
                        </ListItem>
                      );
                    })}
                  </List>
                )}
              </Stack>
            );
          })()}

          {tabId === 'indicator' && (
            <IndicatorSettingsPanel
              boundLayerId={boundLayerId}
              availableFields={dataBindingPanelProps.availableFields}
              indicatorFilterField={indicatorFilterField}
              setIndicatorFilterField={setIndicatorFilterField}
              indicatorFilterOperator={indicatorFilterOperator}
              setIndicatorFilterOperator={setIndicatorFilterOperator}
              indicatorFilterValue={indicatorFilterValue}
              setIndicatorFilterValue={setIndicatorFilterValue}
              indicatorTextTop={indicatorTextTop}
              setIndicatorTextTop={setIndicatorTextTop}
              indicatorTextMiddle={indicatorTextMiddle}
              setIndicatorTextMiddle={setIndicatorTextMiddle}
              indicatorTextBottom={indicatorTextBottom}
              setIndicatorTextBottom={setIndicatorTextBottom}
              indicatorColorTop={indicatorColorTop}
              setIndicatorColorTop={setIndicatorColorTop}
              indicatorColorMiddle={indicatorColorMiddle}
              setIndicatorColorMiddle={setIndicatorColorMiddle}
              indicatorColorBottom={indicatorColorBottom}
              setIndicatorColorBottom={setIndicatorColorBottom}
              indicatorAccent={indicatorAccent}
              setIndicatorAccent={setIndicatorAccent}
              indicatorFaceBg={indicatorFaceBg}
              setIndicatorFaceBg={setIndicatorFaceBg}
              indicatorBgMiddle={indicatorBgMiddle}
              setIndicatorBgMiddle={setIndicatorBgMiddle}
              indicatorShowLastUpdated={indicatorShowLastUpdated}
              setIndicatorShowLastUpdated={setIndicatorShowLastUpdated}
            />
          )}

          {tabId === 'list' && widgetType === 'device_list' && (
            <Stack spacing={2}>
              <Typography variant="subtitle2" fontWeight={700}>List options</Typography>

              <FormControlLabel
                control={
                  <Switch
                    checked={listShowLastOnly}
                    onChange={(_, v) => setListShowLastOnly(v)}
                    size="small"
                  />
                }
                label="Show last location only (per unique entity)"
              />

              <SettingsToggleGroup
                label="Line item icon"
                value={listIconMode}
                onChange={(v) => setListIconMode(v as 'none' | 'symbol')}
                options={[
                  { value: 'none', label: 'None' },
                  { value: 'symbol', label: 'Symbol' },
                ]}
              />

              <SettingsColorPicker
                label="Highlight text color"
                value={listHighlightColor}
                onChange={setListHighlightColor}
                placeholder="#ffeb3b"
              />

              <DeviceListFormatBuilder
                rules={listFormatRules}
                onChange={setListFormatRules}
                availableFields={boundLayerFields}
                fieldValuesMap={fieldValuesMap}
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={listSelectionMode === 'multiple'}
                    onChange={(_, checked) => setListSelectionMode(checked ? 'multiple' : 'single')}
                    size="small"
                  />
                }
                label={`Selection mode: ${listSelectionMode}`}
              />

              <Box>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                  When selection changes
                </Typography>
                {listActions.map((action, ai) => (
                  <FormControlLabel
                    key={action.type}
                    sx={{ display: 'flex', mr: 0 }}
                    control={
                      <Switch
                        size="small"
                        checked={action.enabled}
                        onChange={(_, checked) =>
                          setListActions((prev) =>
                            prev.map((a, i) => (i === ai ? { ...a, enabled: checked } : a)),
                          )
                        }
                      />
                    }
                    label={
                      <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                        {action.type}
                      </Typography>
                    }
                  />
                ))}
              </Box>

            </Stack>
          )}

          {tabId === 'stack' && widgetType === 'widget_stack' && (
            <StackSettingsPanel
              childIds={stackChildIds}
              widgets={allWidgets ?? {}}
              navStyle={stackNavStyle}
              setNavStyle={setStackNavStyle}
              tabLabels={stackTabLabels}
              onTabLabelChange={(childId, label) =>
                setStackTabLabels((prev) => ({ ...prev, [childId]: label }))
              }
              onReorder={setStackChildIds}
              onRemoveChild={(childId) => {
                setStackChildIds((prev) => prev.filter((id) => id !== childId));
                setStackTabLabels((prev) => { const n = { ...prev }; delete n[childId]; return n; });
                onUnstackChild?.(childId);
              }}
            />
          )}

          {tabId === 'group' && widgetType === 'widget_group' && (
            <GroupSettingsPanel
              childIds={groupChildIds}
              widgets={allWidgets ?? {}}
              direction={groupDirection}
              setDirection={setGroupDirection}
              onReorder={setGroupChildIds}
              onRemoveChild={(childId) => {
                setGroupChildIds((prev) => prev.filter((id) => id !== childId));
                onUngroupChild?.(childId);
              }}
            />
          )}

          {tabId === 'appearance' && (
            <AppearancePanel
              cardBackground={cardBackground}
              setCardBackground={setCardBackground}
              cardBorderColor={cardBorderColor}
              setCardBorderColor={setCardBorderColor}
              cardBorderRadius={cardBorderRadius}
              setCardBorderRadius={setCardBorderRadius}
              cardOpacity={cardOpacity}
              setCardOpacity={setCardOpacity}
              contentScale={contentScale}
              setContentScale={setContentScale}
              titleColor={titleColor}
              setTitleColor={setTitleColor}
              titleFontSize={titleFontSize}
              setTitleFontSize={setTitleFontSize}
              contentTextColor={contentTextColor}
              setContentTextColor={setContentTextColor}
              hideHeader={hideHeader}
              setHideHeader={setHideHeader}
              hideLabel={hideLabel}
              setHideLabel={setHideLabel}
            />
          )}

          {tabId === 'actions' && (
            <Stack spacing={2}>
              <Typography variant="subtitle2">Cross-filter targets</Typography>
              <Typography variant="body2" color="text.secondary">
                Enable filtering to other widgets. When this widget triggers a filter event, the selected target widgets will be filtered accordingly.
              </Typography>
              {otherWidgets.length === 0 ? (
                <Typography variant="body2" color="text.disabled">
                  No other widgets on the dashboard yet. Add widgets to the grid to configure cross-filter targets here.
                </Typography>
              ) : (
                <Stack spacing={0.5}>
                  {otherWidgets.map((ow) => {
                    const target = crossFilterTargets.find((t) => t.widgetId === ow.id);
                    const enabled = target?.enabled ?? false;
                    const targetFields = getTargetFields(ow.id);

                    return (
                      <Box key={ow.id} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={1}
                          sx={{ px: 1.5, py: 0.75 }}
                        >
                          {WIDGET_TYPE_ICONS[ow.type] ?? <DataIcon fontSize="small" />}
                          <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap title={ow.title}>
                            {ow.title}
                          </Typography>
                          <Chip label={ow.type} size="small" variant="outlined" sx={{ flexShrink: 0 }} />
                          <Switch
                            size="small"
                            checked={enabled}
                            onChange={(_, checked) => updateCrossFilterTarget(ow.id, { enabled: checked })}
                          />
                        </Stack>
                        <Collapse in={enabled}>
                          <Stack spacing={1.5} sx={{ px: 2, pb: 1.5 }}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  size="small"
                                  checked={target?.renderOnlyWhenFiltered ?? false}
                                  onChange={(_, checked) =>
                                    updateCrossFilterTarget(ow.id, { renderOnlyWhenFiltered: checked })
                                  }
                                />
                              }
                              label={<Typography variant="body2">Render only when filtered</Typography>}
                            />
                            {(target?.fieldMappings ?? []).map((mapping, mi) => (
                              <Stack key={mi} spacing={1}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <TextField
                                    label="Source field"
                                    size="small"
                                    fullWidth
                                    value={mapping.sourceField}
                                    onChange={(e) =>
                                      updateFieldMapping(ow.id, mi, { sourceField: e.target.value })
                                    }
                                  />
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      setCrossFilterTargets((prev) =>
                                        prev.map((t) => {
                                          if (t.widgetId !== ow.id) return t;
                                          return { ...t, fieldMappings: t.fieldMappings.filter((_, i) => i !== mi) };
                                        })
                                      );
                                    }}
                                  >
                                    <DeleteIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </Stack>
                                {targetFields.length > 0 ? (
                                  <SettingsSelect
                                    label="Target field"
                                    value={mapping.targetField}
                                    onChange={(v) => updateFieldMapping(ow.id, mi, { targetField: v })}
                                    options={targetFields.map((f) => ({
                                      value: f.name,
                                      label: `${f.name}`,
                                      icon: getFieldTypeIcon(f.type),
                                    }))}
                                  />
                                ) : (
                                  <TextField
                                    label="Target field"
                                    size="small"
                                    fullWidth
                                    value={mapping.targetField}
                                    onChange={(e) =>
                                      updateFieldMapping(ow.id, mi, { targetField: e.target.value })
                                    }
                                  />
                                )}
                              </Stack>
                            ))}
                            <Button
                              size="small"
                              startIcon={<AddIcon />}
                              onClick={() => {
                                setCrossFilterTargets((prev) =>
                                  prev.map((t) => {
                                    if (t.widgetId !== ow.id) return t;
                                    return {
                                      ...t,
                                      fieldMappings: [...t.fieldMappings, { sourceField: '', targetField: '' }],
                                    };
                                  })
                                );
                              }}
                              sx={{ alignSelf: 'flex-start' }}
                            >
                              Add field mapping
                            </Button>
                          </Stack>
                        </Collapse>
                      </Box>
                    );
                  })}
                </Stack>
              )}
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WidgetSettingsDialog;
